import React from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js'
import { Line } from 'react-chartjs-2'
import { PipeDataStructure } from '../types/pipe'
import './LinearChart.css'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

interface LinearChartProps {
  data: PipeDataStructure
}

const LinearChart: React.FC<LinearChartProps> = ({ data }) => {
  const { phi, inner_radius, outer_radius } = data.Data
  const hasInner = data.pipeinfo?.inner_radius_data === true
  const hasOuter = data.pipeinfo?.outer_radius_data === true
  const hasBothRadii = hasInner && hasOuter

  // Convert phi angles to degrees for labels
  const anglesDegrees = phi.map(angle => ((angle * 180 / Math.PI + 180) % 360))
  
  // Calculate wall thickness for each point (only when both radii present)
  const wallThickness = hasBothRadii
    ? inner_radius.map((internal: number | string, index: number) =>
        Number(outer_radius[index] ?? 0) - Number(internal)
      )
    : []

  const datasets = [
    ...(hasInner
      ? [
          {
            label: 'Internal Radius (mm)',
            data: inner_radius.map((v: number | string) => Number(v)),
            borderColor: 'rgba(74, 144, 226, 0.8)',
            backgroundColor: 'rgba(74, 144, 226, 0.1)',
            borderWidth: 3,
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 5,
            tension: 0.1
          }
        ]
      : []),
    ...(hasOuter
      ? [
          {
            label: 'External Radius (mm)',
            data: outer_radius.map((v: number | string) => Number(v)),
            borderColor: 'rgba(255, 107, 53, 0.8)',
            backgroundColor: 'rgba(255, 107, 53, 0.1)',
            borderWidth: 3,
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 5,
            tension: 0.1
          }
        ]
      : []),
    ...(hasBothRadii
      ? [
          {
            label: 'Wall Thickness (mm)',
            data: wallThickness,
            borderColor: 'rgba(155, 89, 182, 0.8)',
            backgroundColor: 'rgba(155, 89, 182, 0.15)',
            borderWidth: 2,
            fill: true,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.1,
            yAxisID: 'y1'
          }
        ]
      : [])
  ]

  const chartData = {
    labels: anglesDegrees.map(angle => `${Math.round(angle)}°`),
    datasets
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
          legend: {
            position: 'top' as const,
            labels: {
              color: '#000000',
              font: {
                size: 12
              },
              usePointStyle: true,
              padding: 20
            }
          },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderWidth: 1,
        callbacks: {
          title: function(context: any) {
            return `Angle: ${context[0].label}`
          },
          label: function(context: any) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} mm`
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Angle (degrees)',
          color: '#000000',
          font: {
            size: 14,
                weight: 'bold' as const
          }
        },
        ticks: {
          color: 'rgba(0, 0, 0, 0.8)',
          maxTicksLimit: 20,
          font: {
            size: 10
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false
        }
      },
      y: {
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Radius (mm)',
          color: '#000000',
          font: {
            size: 14,
                weight: 'bold' as const
          }
        },
        ticks: {
          color: 'rgba(0, 0, 0, 0.8)',
          font: {
            size: 10
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false
        }
      },
      y1: {
        type: 'linear' as const,
        display: hasBothRadii,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Wall Thickness (mm)',
          color: 'rgba(155, 89, 182, 0.8)',
          font: {
            size: 14,
                weight: 'bold' as const
          }
        },
        ticks: {
          color: 'rgba(155, 89, 182, 0.8)',
          font: {
            size: 10
          }
        },
        grid: {
          drawOnChartArea: false,
        },
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutCubic' as const
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    }
  }

  return (
    <div className="linear-chart-container">
      <div className="chart-wrapper">
        <Line data={chartData} options={options} />
      </div>
    </div>
  )
}

export default LinearChart
